import {
  CASINO_HIDDEN_CARD,
  MONEY_WHEEL_SEGMENTS,
  SLOT_STOPS,
  type CasinoCard,
  type CasinoPrivate,
  type CasinoState,
} from "@townsy/contracts";

/**
 * Pure helpers the casino's overlay and 3D share (ADR-0016): reading the server's state, card and chip presentation,
 * countdowns, and the reel / wheel animation maths. Nothing here decides an outcome; it only displays the server's.
 */

/** The shared state if it looks like the casino's (the server sends it; this only guards against a wrong room). */
export function asCasinoState(x: unknown): CasinoState | null {
  if (!x || typeof x !== "object") return null;
  const s = x as Partial<CasinoState>;
  if (!Array.isArray(s.tables) || !Array.isArray(s.slots) || !s.wheel || !Array.isArray(s.drinks)) return null;
  return s as CasinoState;
}

export function asCasinoPrivate(x: unknown): CasinoPrivate | null {
  if (!x || typeof x !== "object" || !("near" in x)) return null;
  return x as CasinoPrivate;
}

// ---------------------------------------------------------------------------------------------------------------------
// Cards and chips
// ---------------------------------------------------------------------------------------------------------------------

const SUIT_GLYPH: Record<string, string> = { S: "♠", H: "♥", D: "♦", C: "♣" };
const SUIT_NAME: Record<string, string> = { S: "spades", H: "hearts", D: "diamonds", C: "clubs" };
const RANK_NAME: Record<string, string> = { A: "ace", T: "10", J: "jack", Q: "queen", K: "king" };

/** "A♠", "10♥"; the hidden hole card is "?". */
export function cardLabel(card: CasinoCard): string {
  if (card === CASINO_HIDDEN_CARD) return "?";
  const r = card[0] === "T" ? "10" : (card[0] ?? "?");
  return `${r}${SUIT_GLYPH[card[1] ?? ""] ?? ""}`;
}

/** Spoken form for screen readers: "ace of spades", "10 of hearts", "face-down card". */
export function cardSpoken(card: CasinoCard): string {
  if (card === CASINO_HIDDEN_CARD) return "face-down card";
  return `${RANK_NAME[card[0] ?? ""] ?? card[0]} of ${SUIT_NAME[card[1] ?? ""] ?? ""}`;
}

export function isRedSuit(card: CasinoCard): boolean {
  return card[1] === "H" || card[1] === "D";
}

/** Chip colours by value (original palette; play chips only). */
export const CHIP_VALUES = [50, 25, 10, 5, 1] as const;
export const CHIP_COLORS: Record<(typeof CHIP_VALUES)[number], string> = {
  50: "#24242C",
  25: "#2E8B57",
  10: "#2F5FD0",
  5: "#C8323C",
  1: "#F1EDE4",
};

/** The chips that make up an amount, largest first (at most `max` chips; the rest is shown by the top chip's value). */
export function chipStack(amount: number, max = 10): Array<(typeof CHIP_VALUES)[number]> {
  const out: Array<(typeof CHIP_VALUES)[number]> = [];
  let left = Math.max(0, Math.floor(amount));
  for (const v of CHIP_VALUES) {
    while (left >= v && out.length < max) {
      out.push(v);
      left -= v;
    }
  }
  return out;
}

export function chipsText(n: number): string {
  return n === 1 ? "1 play chip" : `${n} play chips`;
}

// ---------------------------------------------------------------------------------------------------------------------
// Time
// ---------------------------------------------------------------------------------------------------------------------

/** Whole seconds left of a server countdown sent as `endsIn` ms when the state arrived at `receivedAt`. */
export function secondsLeft(endsIn: number | null | undefined, receivedAt: number, now: number): number | null {
  if (endsIn === null || endsIn === undefined) return null;
  return Math.max(0, Math.ceil((endsIn - (now - receivedAt)) / 1000));
}

// ---------------------------------------------------------------------------------------------------------------------
// Animation maths (the outcome is the server's: these only move the drawing to it)
// ---------------------------------------------------------------------------------------------------------------------

export const easeOutCubic = (t: number) => 1 - (1 - Math.min(1, Math.max(0, t))) ** 3;

/** Angle of one wheel segment (radians). */
export const WHEEL_SEGMENT_ANGLE = (Math.PI * 2) / MONEY_WHEEL_SEGMENTS.length;

/**
 * Wheel rotation (radians, positive = clockwise as seen from the front) that puts `segment` under the pointer at the
 * top, segments being drawn clockwise from the top.
 */
export function wheelRestAngle(segment: number): number {
  return -segment * WHEEL_SEGMENT_ANGLE;
}

/** The segment under the pointer for a wheel rotation (inverse of wheelRestAngle). */
export function segmentAtAngle(angle: number): number {
  const n = MONEY_WHEEL_SEGMENTS.length;
  return (((Math.round(-angle / WHEEL_SEGMENT_ANGLE) % n) + n) % n);
}

/**
 * A spin from `from` that ends exactly on `segment` after at least `turns` whole turns (clockwise), as a function of
 * progress 0..1 (eased: fast start, slow stop).
 */
export function wheelSpinAngle(from: number, segment: number, turns: number, progress: number): number {
  const target = wheelRestAngle(segment);
  // Clockwise = decreasing angle here: travel down to the target, plus whole turns.
  let travel = from - target;
  travel = ((travel % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  travel += turns * Math.PI * 2;
  return from - travel * easeOutCubic(progress);
}

/**
 * Reel position (in stops, fractional) during a spin from stop `from` to stop `to`, symbols moving down (the index at
 * the payline decreases), with `turns` whole turns of the strip first.
 */
export function reelSpinPosition(from: number, to: number, turns: number, progress: number): number {
  let travel = (((from - to) % SLOT_STOPS) + SLOT_STOPS) % SLOT_STOPS;
  travel += turns * SLOT_STOPS;
  return from - travel * easeOutCubic(progress);
}

/** Wrap a reel position into [0, SLOT_STOPS). */
export function wrapStop(p: number): number {
  return ((p % SLOT_STOPS) + SLOT_STOPS) % SLOT_STOPS;
}

/** The slot machines' names (their toppers in 3D and the big view's title). Original names. */
export const SLOT_MACHINE_NAMES = ["LUCKY 7s", "BELL RINGER", "CHERRY POP", "GOLD BARS", "STAR LINE", "TRIPLE 7", "FRUIT FIESTA", "TOWNSY GOLD"] as const;
